'use client';

import { useState } from 'react';
import { LinkButton } from '../../../../components/form';
import { CarouselModal, ImageView } from '../../../../components/ui';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { VariantType } from '../../../../types';
import type { LotteryBenefitGoods, LotteryCustomOption, LotteryDrawHistoryLineup } from '../../types';
import { pickText } from '../../utils';
import styles from './LotteryGoodsList.module.scss';
import raffleStyles from './LotteryGoodsList.raffle.module.scss';

export interface LotteryGoodsListProps {
  goodsList: Array<LotteryBenefitGoods>;
  linupId: number;
  lotteryDrawHistory?: Array<LotteryDrawHistoryLineup>;
  liKeyPrefix?: string;
  lotteryStatus: string;
  locale?: 'ja' | 'en';
  customOptions?: LotteryCustomOption;
  // 外部依存関数・定数
  isGotGoods: (
    linupId: number,
    goods: LotteryBenefitGoods,
    lotteryDrawHistory?: Array<LotteryDrawHistoryLineup>,
  ) => boolean;
  formatCurrency: (value: number) => string;
  LOTTERY_STATUS: { later: { value: string } };
  getLabelSrc: string;
  variant?: VariantType;
}

function LotteryGoodsList({
  goodsList,
  linupId,
  lotteryDrawHistory,
  liKeyPrefix = 'goods',
  lotteryStatus,
  locale = 'ja',
  customOptions,
  isGotGoods,
  formatCurrency,
  LOTTERY_STATUS,
  getLabelSrc,
  variant = 'default',
}: LotteryGoodsListProps) {
  const [isScrollModalOpen, setIsScrollModalOpen] = useState(false);
  const [initShowImageUrlIndex, setInitShowImageUrlIndex] = useState(0);

  // variantに応じてスタイルを決定
  // var(--color-primary)の指定があるため、出し分ける
  const currentStyles = variant === 'raffle' ? raffleStyles : styles;

  const openGoodsModal = (e: React.MouseEvent, goods: LotteryBenefitGoods) => {
    e.preventDefault();
    const clickedIndex = goodsList.findIndex((item) => item.id === goods.id);
    setInitShowImageUrlIndex(clickedIndex);
    setIsScrollModalOpen(true);
  };

  const getLocalizedText = (textJa: string, textEn?: string) => {
    if (!textEn) return textJa;
    return pickText(locale, textJa, textEn);
  };

  // 終了
  const isNotLater = lotteryStatus !== LOTTERY_STATUS.later.value;

  return (
    <>
      <ul className={currentStyles.componentStyle}>
        {goodsList.map((goodsItem) => {
          const isGot = isGotGoods(linupId, goodsItem, lotteryDrawHistory);
          return (
            <li key={`${liKeyPrefix}_${linupId}_${goodsItem.id}`}>
              {/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
              <a className={currentStyles.goodsLink} onClick={(e) => openGoodsModal(e, goodsItem)}>
                <div className={currentStyles.goodsImageWrapper}>
                  <div className={currentStyles.goodsImage} data-testid='prize-image'>
                    <ImageView src={goodsItem.imageUrl} alt='景品' />
                    {isGot && <img className={currentStyles.gotLabel} src={getLabelSrc} alt='GET' />}
                  </div>
                </div>
                <p
                  className={`${currentStyles.goodsName} ${utilsStyles.textBold} ${customOptions?.showAllPrizeGoodsNames === '1' ? currentStyles.showAllPrizeGoodsNames : ''}`}
                >
                  {getLocalizedText(goodsItem.name, goodsItem.nameEn)}
                </p>
                {/* 開催終了後は出さない */}
                {isNotLater && goodsItem.isStockLimit && (
                  <p className={`${currentStyles.itemStock} ${utilsStyles.textXSmall} ${utilsStyles.textRight}`}>
                    {formatCurrency(goodsItem.stock)}個
                  </p>
                )}
              </a>
            </li>
          );
        })}
        {dummy(currentStyles)}
      </ul>
      <CarouselModal
        initShowIndex={initShowImageUrlIndex}
        isOpen={isScrollModalOpen}
        close={() => setIsScrollModalOpen(false)}
        childrenArr={goodsList.map((goods) => {
          const isGot = isGotGoods(linupId, goods, lotteryDrawHistory);
          return (
            <div className={currentStyles.goodsModal} key={goods.id}>
              <ImageView src={goods.imageUrl} alt={'景品'} isFreeSize />
              <div className={currentStyles.modalPadding}>
                <div className={currentStyles.marginTop}>
                  <em>{goods.name}</em>
                </div>
                {isGot && (
                  <div className={utilsStyles.mtXSmall}>
                    <LinkButton href={`/account/collection/${goods.id}`} size='fullWidth' outlined>
                      獲得した景品を見る
                    </LinkButton>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      />
    </>
  );
}

function dummy(currentStyles: typeof styles) {
  const result = [];
  for (let i = 0; i < 5; i++) {
    result.push(
      <li key={`dummy_${i}`} className={currentStyles.dummy}>
        <div className={currentStyles.goodsLink} />
      </li>,
    );
  }
  return result;
}

export default LotteryGoodsList;
